import React, { useEffect, useMemo, useRef, useState } from "react"
import { motion } from "framer-motion"
import { useHistory, useLocation, useParams } from "react-router-dom"
import { HiOutlinePaperAirplane, HiOutlineArrowLeft, HiOutlineChatBubbleLeftRight, HiOutlineFlag, HiOutlineLockClosed } from "react-icons/hi2"
import API from "../../Common/API"
import dummy from "../../Assets/Images/dummyProfile.jpeg"
import ReportUserDialog from "../../Components/ReportUserDialog"
import { useLanguage } from "../../Common/LanguageContext"

const isPaidMember = (user) => {
    if (!user) return false
    if (user.role === "Super_Admin" || user.role === "Admin") return true
    if (!user.isPaid) return false
    if (!user.planValidityDate) return false
    return new Date(user.planValidityDate) > new Date()
}

const POLL_MS = 3000

const formatName = (u) => [u?.firstName, u?.lastName].filter(Boolean).join(" ") || u?.refId || "Member"
const profileImg = (u) => u?.images?.[0]?.imageUrl || dummy

const supportsNotifications = () => typeof window !== "undefined" && "Notification" in window
const requestNotificationPermission = async () => {
    if (!supportsNotifications()) return "unsupported"
    if (Notification.permission === "granted") return "granted"
    if (Notification.permission === "denied") return "denied"
    try { return await Notification.requestPermission() } catch { return "denied" }
}
const fireMessageNotification = (conv) => {
    if (!supportsNotifications() || Notification.permission !== "granted") return
    if (typeof document !== "undefined" && document.visibilityState === "visible" && document.hasFocus()) return
    try {
        const n = new Notification(`New message from ${formatName(conv.otherUser)}`, {
            body: conv.lastMessage?.body || "You have a new message",
            icon: profileImg(conv.otherUser),
            tag: `chat-${conv.otherUser._id}`,
            silent: false,
        })
        n.onclick = () => {
            window.focus()
            const base = window.location.pathname.startsWith("/member/") ? "/member/chat" : "/chat"
            window.location.assign(`${base}/${conv.otherUser._id}`)
            n.close()
        }
    } catch (_) { /* ignore */ }
}

const formatTime = (iso) => {
    if (!iso) return ""
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ""
    const now = new Date()
    const sameDay = d.toDateString() === now.toDateString()
    if (sameDay) {
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    }
    return d.toLocaleDateString([], { month: "short", day: "numeric" })
}

const OnlineDot = ({ online }) => (
    <span
        className={`inline-block h-2.5 w-2.5 rounded-full ${online ? "bg-[#1a8a3a]" : "bg-slate-300"}`}
        title={online ? "Online" : "Offline"}
    />
)

const ConversationItem = ({ conv, active, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className={`flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition ${
            active ? "border-primeColor bg-primeSoft/40" : "border-transparent hover:bg-lightGrey/50"
        }`}
    >
        <div className="relative">
            <img src={profileImg(conv.otherUser)} alt={formatName(conv.otherUser)} className="h-11 w-11 rounded-full object-cover" />
            <span className="absolute -right-0.5 -bottom-0.5"><OnlineDot online={conv.otherUser.online} /></span>
        </div>
        <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-bold text-tColor">{formatName(conv.otherUser)}</span>
                <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.14em] text-tpColor">
                    {formatTime(conv.lastMessage.createdAt)}
                </span>
            </div>
            <div className="flex items-center justify-between gap-2">
                <span className="truncate text-xs text-tpColor">{conv.lastMessage.body}</span>
                {conv.unread > 0 && (
                    <span className="ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primeColor px-1.5 text-[10px] font-bold text-white">
                        {conv.unread}
                    </span>
                )}
            </div>
        </div>
    </button>
)

const Chat = () => {
    const history = useHistory()
    const location = useLocation()
    const params = useParams()
    const { t } = useLanguage()
    const activeUserId = params.userId || null
    // Works in both /chat/:userId (admin) and /member/chat/:userId (member)
    const chatBase = location.pathname.startsWith("/member/") ? "/member/chat" : "/chat"

    const [conversations, setConversations] = useState([])
    const [messages, setMessages] = useState([])
    const [otherUser, setOtherUser] = useState(null)
    const [text, setText] = useState("")
    const [sending, setSending] = useState(false)
    const [conversationsFilter, setConversationsFilter] = useState("")
    const [userSearchResults, setUserSearchResults] = useState([])
    const [userSearching, setUserSearching] = useState(false)
    const [error, setError] = useState(null)
    const [notifPerm, setNotifPerm] = useState(supportsNotifications() ? Notification.permission : "unsupported")
    const [reportTarget, setReportTarget] = useState(null)
    const searchDebounceRef = useRef(null)

    const messagesEndRef = useRef(null)
    const lastConvSnapshotRef = useRef({}) // otherUserId -> last message id seen
    const myUserDetails = useMemo(() => {
        try { return JSON.parse(localStorage.getItem("userDetails") || "{}") }
        catch { return {} }
    }, [])

    const loadConversations = async () => {
        try {
            const res = await API.get("/chat/conversations")
            const list = res.data?.data || []
            // Detect new incoming messages and fire desktop notifications
            const myId = myUserDetails?._id
            const prev = lastConvSnapshotRef.current
            list.forEach((conv) => {
                const lastId = conv.lastMessage?._id
                const fromOther = conv.lastMessage?.from && conv.lastMessage.from !== myId
                const prevId = prev[conv.otherUser._id]
                // Only notify when this is a new message FROM the other party
                if (fromOther && prevId !== undefined && prevId !== lastId) {
                    fireMessageNotification(conv)
                }
            })
            const next = {}
            list.forEach((c) => { next[c.otherUser._id] = c.lastMessage?._id })
            lastConvSnapshotRef.current = next
            setConversations(list)
        } catch (err) {
            // silent
        }
    }

    const enableNotifications = async () => {
        const result = await requestNotificationPermission()
        setNotifPerm(result)
    }

    const loadMessages = async (uid) => {
        if (!uid) return
        try {
            const res = await API.get(`/chat/messages/${uid}`)
            setMessages(res.data?.data?.messages || [])
            setOtherUser(res.data?.data?.otherUser || null)
            setError(null)
        } catch (err) {
            setError(err?.response?.data?.error || "Could not load conversation")
            setMessages([])
            setOtherUser(null)
        }
    }

    useEffect(() => { loadConversations() }, [])
    useEffect(() => { loadMessages(activeUserId) }, [activeUserId])

    // Polling
    useEffect(() => {
        const id = setInterval(() => {
            loadConversations()
            if (activeUserId) loadMessages(activeUserId)
        }, POLL_MS)
        return () => clearInterval(id)
    }, [activeUserId])

    useEffect(() => {
        if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" })
    }, [messages.length])

    const filteredConvos = useMemo(() => {
        if (!conversationsFilter.trim()) return conversations
        const q = conversationsFilter.toLowerCase()
        return conversations.filter((c) => {
            const u = c.otherUser
            return (
                formatName(u).toLowerCase().includes(q) ||
                (u?.refId || "").toLowerCase().includes(q) ||
                (u?.contactInformation?.mobileNumber || u?.mobileNumber || "").includes(q)
            )
        })
    }, [conversations, conversationsFilter])

    // Search all users when filter text doesn't match existing convos
    useEffect(() => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
        const q = conversationsFilter.trim()
        if (q.length < 2) { setUserSearchResults([]); return }
        searchDebounceRef.current = setTimeout(async () => {
            setUserSearching(true)
            try {
                const res = await API.get(`/user/getAllUsers?search=${encodeURIComponent(q)}`)
                const users = res.data?.data || []
                const existingIds = new Set(conversations.map((c) => c.otherUser._id))
                setUserSearchResults(users.filter((u) => !existingIds.has(u._id)))
            } catch {
                setUserSearchResults([])
            } finally {
                setUserSearching(false)
            }
        }, 400)
        return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current) }
    }, [conversationsFilter, conversations])

    const send = async (e) => {
        e?.preventDefault?.()
        if (!activeUserId || !text.trim() || sending) return
        const body = text
        setText("")
        setSending(true)
        try {
            await API.post("/chat/send", { toUserId: activeUserId, message: body })
            await loadMessages(activeUserId)
            await loadConversations()
        } catch (err) {
            setError(err?.response?.data?.error || "Could not send message")
            setText(body) // restore so user can retry
        } finally {
            setSending(false)
        }
    }

    // ── Paid-member gate ────────────────────────────────────────────────────────
    if (!isPaidMember(myUserDetails)) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex min-h-[60vh] flex-col items-center justify-center gap-6 px-4 py-16 text-center"
            >
                <div className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-primeColor/20 bg-primeSoft">
                    <HiOutlineLockClosed className="h-10 w-10 text-primeColor" />
                </div>
                <div className="max-w-sm">
                    <h2 className="text-2xl font-semibold text-tColor">Chat is a Premium Feature</h2>
                    <p className="mt-3 text-base leading-7 text-tpColor">
                        Private messaging is available exclusively for paid members. Upgrade your plan to start chatting with your matches.
                    </p>
                </div>
                <div className="flex flex-col items-center gap-3">
                    <button
                        type="button"
                        onClick={() => history.push("/member/plans")}
                        className="rounded-2xl bg-primeColor px-8 py-3 text-sm font-bold text-white shadow-soft transition hover:bg-primeDark"
                    >
                        View Plans &amp; Upgrade
                    </button>
                    <p className="text-xs text-tpColor">
                        Already upgraded? Contact our support team to activate your plan.
                    </p>
                </div>
            </motion.div>
        )
    }

    return (
        <motion.div className="space-y-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-primeColor">{t("privateChat")}</p>
                    <h2 className="text-2xl font-semibold text-tColor">{t("conversations")}</h2>
                    <p className="text-sm text-tpColor">{t("phoneHidden")}</p>
                </div>
                {notifPerm === "default" && (
                    <button
                        type="button"
                        onClick={enableNotifications}
                        className="inline-flex items-center gap-2 rounded-full border border-borderColor bg-white px-4 py-2 text-xs font-bold text-tColor shadow-sm hover:border-primeColor hover:text-primeColor"
                    >
                        🔔 {t("enableNotifications")}
                    </button>
                )}
                {notifPerm === "granted" && (
                    <span className="inline-flex items-center gap-2 rounded-full border border-[#cfe8d4] bg-[#f2fbf4] px-3 py-1 text-xs font-bold text-[#1a8a3a]">
                        🔔 {t("notificationsEnabled")}
                    </span>
                )}
                {notifPerm === "denied" && (
                    <span className="inline-flex items-center gap-2 rounded-full border border-[#efcaca] bg-[#fff5f5] px-3 py-1 text-xs font-bold text-[#a03e3e]" title="Allow notifications in your browser settings">
                        🔔 {t("notificationsBlocked")}
                    </span>
                )}
            </div>

            <div className="grid gap-4 md:grid-cols-[320px_minmax(0,1fr)] md:items-stretch">
                {/* Left: conversation list (hidden on mobile when a chat is open) */}
                <div className={`rounded-2xl border border-borderColor bg-white p-3 ${activeUserId ? "hidden md:block" : ""}`}>
                    <input
                        type="text"
                        value={conversationsFilter}
                        onChange={(e) => setConversationsFilter(e.target.value)}
                        placeholder={t("searchConversations")}
                        className="mb-3 w-full rounded-full border border-borderColor bg-lightGrey/40 px-4 py-2 text-sm outline-none focus:border-primeColor"
                    />
                    <div className="space-y-1 max-h-[60vh] md:max-h-[70vh] overflow-y-auto">
                        {filteredConvos.length === 0 && userSearchResults.length === 0 && !userSearching ? (
                            <div className="px-3 py-6 text-center text-sm text-tpColor">
                                <HiOutlineChatBubbleLeftRight className="mx-auto mb-2 h-8 w-8 opacity-60" />
                                {conversationsFilter.trim() ? "No matches found" : t("noConversations")}
                            </div>
                        ) : null}
                        {filteredConvos.map((c) => (
                            <ConversationItem
                                key={c.otherUser._id}
                                conv={c}
                                active={c.otherUser._id === activeUserId}
                                onClick={() => history.push(`${chatBase}/${c.otherUser._id}`)}
                            />
                        ))}
                        {conversationsFilter.trim().length >= 2 && (userSearching || userSearchResults.length > 0) && (
                            <div className="pt-2">
                                <p className="px-2 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-tpColor">
                                    {userSearching ? "Searching users…" : "Start a new chat"}
                                </p>
                                {userSearchResults.map((u) => (
                                    <button
                                        key={u._id}
                                        type="button"
                                        onClick={() => { setConversationsFilter(""); history.push(`${chatBase}/${u._id}`) }}
                                        className="flex w-full items-center gap-3 rounded-xl border border-transparent px-3 py-3 text-left transition hover:bg-lightGrey/50"
                                    >
                                        <img src={profileImg(u)} alt={formatName(u)} className="h-11 w-11 rounded-full object-cover" />
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm font-bold text-tColor">{formatName(u)}</p>
                                            <p className="text-xs text-tpColor">{u.refId} · {u?.contactInformation?.mobileNumber || u?.mobileNumber || ""}</p>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right: active conversation */}
                <div className={`flex flex-col rounded-2xl border border-borderColor bg-white ${!activeUserId ? "hidden md:flex" : "flex"}`}>
                    {activeUserId && otherUser ? (
                        <>
                            <div className="flex items-center gap-3 border-b border-borderColor p-3">
                                <button
                                    type="button"
                                    className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-full bg-lightGrey/60 text-tColor"
                                    onClick={() => history.push(chatBase)}
                                >
                                    <HiOutlineArrowLeft className="h-5 w-5" />
                                </button>
                                <div className="relative">
                                    <img src={profileImg(otherUser)} alt={formatName(otherUser)} className="h-10 w-10 rounded-full object-cover" />
                                    <span className="absolute -right-0.5 -bottom-0.5"><OnlineDot online={otherUser.online} /></span>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-base font-bold text-tColor">{formatName(otherUser)}</p>
                                    <p className="text-xs text-tpColor">
                                        {otherUser.online ? t("online") : otherUser.lastSeen ? `${t("lastSeen")} ${formatTime(otherUser.lastSeen)}` : t("offline")}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setReportTarget(otherUser)}
                                    className="inline-flex items-center gap-1 rounded-full border border-[#efcaca] bg-white px-3 py-1.5 text-xs font-bold text-[#a03e3e] hover:bg-[#fff5f5]"
                                    title={t("reportTip")}
                                >
                                    <HiOutlineFlag className="h-4 w-4" />
                                    {t("report")}
                                </button>
                            </div>

                            <div className="min-h-0 flex-1 overflow-y-auto p-4 space-y-3 max-h-[55vh] md:max-h-[60vh]">
                                {messages.length === 0 && (
                                    <p className="rounded-xl bg-lightGrey/40 p-4 text-center text-sm text-tpColor">
                                        {t("sayHello")}
                                    </p>
                                )}
                                {messages.map((m) => {
                                    const mine = m.from === myUserDetails?._id
                                    return (
                                        <div key={m._id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                            <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm shadow-sm ${
                                                mine ? "bg-primeColor text-white" : "bg-lightGrey/70 text-tColor"
                                            }`}>
                                                <p className="whitespace-pre-wrap break-words">{m.body}</p>
                                                <p className={`mt-1 text-[10px] ${mine ? "text-white/80" : "text-tpColor"}`}>
                                                    {formatTime(m.createdAt)}{mine && m.readAt ? ` · ${t("readReceipt")}` : ""}
                                                </p>
                                            </div>
                                        </div>
                                    )
                                })}
                                <div ref={messagesEndRef} />
                            </div>

                            {error && (
                                <p className="border-t border-borderColor bg-[#fff5f5] px-4 py-2 text-xs text-[#a03e3e]">{error}</p>
                            )}

                            <form onSubmit={send} className="flex items-center gap-2 border-t border-borderColor p-3">
                                <input
                                    type="text"
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder={t("typingPlaceholder")}
                                    className="flex-1 rounded-full border border-borderColor bg-white px-4 py-2 text-sm outline-none focus:border-primeColor"
                                />
                                <button
                                    type="submit"
                                    disabled={!text.trim() || sending}
                                    className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primeColor text-white shadow-sm transition hover:bg-primeDark disabled:cursor-not-allowed disabled:opacity-50"
                                    aria-label={t("sendMessage")}
                                >
                                    <HiOutlinePaperAirplane className="h-5 w-5" />
                                </button>
                            </form>
                        </>
                    ) : (
                        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center text-tpColor">
                            <HiOutlineChatBubbleLeftRight className="h-10 w-10 opacity-50" />
                            <p className="text-sm">{t("pickConversation")}</p>
                        </div>
                    )}
                </div>
            </div>

            <ReportUserDialog
                open={!!reportTarget}
                target={reportTarget}
                onClose={() => setReportTarget(null)}
                onReported={() => {
                    // After reporting, the user is also blocked → leave the chat thread
                    setReportTarget(null)
                    history.push(chatBase)
                }}
            />
        </motion.div>
    )
}

export default Chat
